import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Clock, CheckCircle, PlayCircle, Lock, LockOpen } from "@phosphor-icons/react";

// Helpers
function detectVideoSource(url) {
  if (!url) return { kind: "none" };
  if (url.includes("youtube.com/watch?v=") || url.includes("youtu.be/")) {
    let id;
    try {
      if (url.includes("youtu.be/")) id = url.split("youtu.be/")[1].split(/[?&]/)[0];
      else id = new URL(url).searchParams.get("v");
    } catch (e) { id = null; }
    return { kind: "youtube", id, embed: id ? `https://www.youtube.com/embed/${id}?enablejsapi=1&rel=0` : url };
  }
  if (url.includes("drive.google.com/file/d/")) {
    const id = url.split("/file/d/")[1].split("/")[0];
    return { kind: "drive", embed: `https://drive.google.com/file/d/${id}/preview` };
  }
  return { kind: "other", embed: url };
}

// Lazy-load YouTube IFrame API
let _ytReadyPromise = null;
function loadYouTubeApi() {
  if (_ytReadyPromise) return _ytReadyPromise;
  _ytReadyPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => resolve(window.YT);
  });
  return _ytReadyPromise;
}

export default function TakeAssessment() {
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [training, setTraining] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState("video"); // video | quiz
  const [watchedPercent, setWatchedPercent] = useState(0);
  const ytPlayerRef = useRef(null);
  const watchTimerRef = useRef(null);
  const startTimeRef = useRef(Date.now());

  const start = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/training/${trainingId}/attempt/start`, {}, { withCredentials: true });
      setAttempt(res.data.attempt);
      setTraining(res.data.training);
      setQuestions(res.data.questions);
      setWatchedPercent(res.data.attempt?.watched_percent || 0);

      const minWatch = res.data.training?.min_watch_percent || 0;
      const hasVideo = !!res.data.training?.recording_url;
      // Skip video stage if no video or no gate
      if (!hasVideo || minWatch === 0) setStage("quiz");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to start assessment");
      navigate("/training");
    } finally {
      setLoading(false);
    }
  }, [trainingId, navigate]);

  useEffect(() => { start(); }, [start]);

  const reportWatch = useCallback(async (pct) => {
    if (!attempt) return;
    try {
      await axios.post(`${API}/attempts/${attempt.attempt_id}/watch-progress`, { watched_percent: Math.round(pct) }, { withCredentials: true });
    } catch (e) { console.debug("swallowed error:", e); }
  }, [attempt]);

  // Video tracking effect (after attempt + training loaded and we're on video stage)
  useEffect(() => {
    if (!training?.recording_url || stage !== "video" || !attempt) return;
    const src = detectVideoSource(training.recording_url);

    if (src.kind === "youtube" && src.id) {
      let cancelled = false;
      loadYouTubeApi().then((YT) => {
        if (cancelled) return;
        ytPlayerRef.current = new YT.Player("yt-player", {
          videoId: src.id,
          playerVars: { rel: 0, modestbranding: 1 },
          events: {
            onReady: () => {
              watchTimerRef.current = setInterval(() => {
                try {
                  const cur = ytPlayerRef.current.getCurrentTime();
                  const dur = ytPlayerRef.current.getDuration();
                  if (dur > 0) {
                    const pct = Math.min(100, Math.round((cur / dur) * 100));
                    setWatchedPercent((prev) => {
                      const next = Math.max(prev, pct);
                      if (next - prev >= 5) reportWatch(next);
                      return next;
                    });
                  }
                } catch (e) { console.debug("ignored error:", e); }
              }, 2000);
            },
            onStateChange: (e) => {
              if (e.data === window.YT.PlayerState.ENDED) {
                setWatchedPercent(100);
                reportWatch(100);
              }
            },
          },
        });
      });
      return () => {
        cancelled = true;
        if (watchTimerRef.current) clearInterval(watchTimerRef.current);
        try { ytPlayerRef.current?.destroy?.(); } catch (e) { console.debug("ignored error:", e); }
      };
    }

    // Drive / other — fall back to wall-clock timer. Assume avg lecture = 600s; user must spend (min_watch_percent% of 600s) on page.
    const min = training.min_watch_percent || 0;
    const targetSeconds = Math.max(60, Math.min(600, 600 * min / 100)); // 1..10 min cap
    watchTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const pct = Math.min(100, Math.round((elapsed / targetSeconds) * 100));
      setWatchedPercent((prev) => {
        const next = Math.max(prev, pct);
        if (next - prev >= 5) reportWatch(next);
        if (next >= 100 && watchTimerRef.current) clearInterval(watchTimerRef.current);
        return next;
      });
    }, 2000);
    return () => { if (watchTimerRef.current) clearInterval(watchTimerRef.current); };
  }, [training, stage, attempt, reportWatch]);

  const proceedToQuiz = async () => {
    await reportWatch(watchedPercent);
    setStage("quiz");
  };

  const submit = async () => {
    const payload = {
      answers: questions.map((q) => ({
        question_id: q.question_id,
        answer: answers[q.question_id] || "",
      })),
    };
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/attempts/${attempt.attempt_id}/submit`, payload, { withCredentials: true });
      toast.success(`Score: ${res.data.score}% • ${res.data.result.toUpperCase()}`);
      navigate(`/training/result/${attempt.attempt_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const minWatch = training?.min_watch_percent || 0;
  const videoUnlocked = watchedPercent >= minWatch;
  const answered = Object.values(answers).filter((v) => (v || "").trim()).length;
  const videoSrc = detectVideoSource(training?.recording_url || "");

  // ============ Video stage ============
  if (stage === "video") {
    return (
      <div className="min-h-screen bg-background" data-testid="take-assessment-video">
        <header className="glass-header border-b border-black/10 sticky top-0 z-50">
          <div className="flex items-center justify-between px-4 md:px-6 h-16">
            <Button variant="ghost" size="sm" onClick={() => navigate("/training")}><ArrowLeft size={18} className="mr-2" />Exit</Button>
            <div className="flex items-center gap-3 text-sm">
              {videoUnlocked ? <LockOpen size={16} className="text-green-600" /> : <Lock size={16} className="text-amber-600" />}
              <span className="font-medium">{watchedPercent}% watched • {minWatch}% required</span>
            </div>
          </div>
        </header>

        <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><PlayCircle size={20} /> Watch the Training Recording</CardTitle>
              <CardDescription>
                You must watch at least <strong>{minWatch}%</strong> of this video before the assessment becomes available.
                {videoSrc.kind !== "youtube" && (
                  <span className="block text-xs mt-1">Note: progress is tracked by time on page since this video is not on YouTube.</span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="aspect-video rounded-md overflow-hidden bg-black">
                {videoSrc.kind === "youtube" ? (
                  <div id="yt-player" className="w-full h-full" />
                ) : (
                  <iframe src={videoSrc.embed} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen title="Training recording" />
                )}
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Watch progress</span>
                  <span>{watchedPercent}% / {minWatch}%</span>
                </div>
                <Progress value={Math.min(100, (watchedPercent / Math.max(1, minWatch)) * 100)} />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate("/training")}>Save & Exit</Button>
            <Button
              onClick={proceedToQuiz}
              disabled={!videoUnlocked}
              data-testid="proceed-to-quiz-btn"
            >
              {videoUnlocked ? <LockOpen size={16} className="mr-2" /> : <Lock size={16} className="mr-2" />}
              {videoUnlocked ? "Start Assessment" : `Watch ${minWatch - watchedPercent}% more to unlock`}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ============ Quiz stage ============
  return (
    <div className="min-h-screen bg-background" data-testid="take-assessment">
      <header className="glass-header border-b border-black/10 sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 md:px-6 h-16">
          <Button variant="ghost" size="sm" onClick={() => navigate("/training")}><ArrowLeft size={18} className="mr-2" />Exit</Button>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            {minWatch > 0 && training?.recording_url && (
              <Badge variant="secondary" className="text-xs"><PlayCircle size={12} className="mr-1" /> {watchedPercent}% watched</Badge>
            )}
            <Clock size={16} />
            <span>{answered} / {questions.length} answered</span>
          </div>
        </div>
      </header>

      <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Assessment — Attempt #{attempt?.attempt_number}</CardTitle>
            <CardDescription>
              Answer all questions. Short answers are graded by AI against the expected concept.
              {attempt?.attempt_number > 1 && <span className="block mt-1 text-xs text-amber-700">This is a retake — your question set has been refreshed where possible.</span>}
            </CardDescription>
          </CardHeader>
        </Card>

        {questions.map((q, idx) => (
          <Card key={q.question_id} data-testid={`q-card-${idx}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground">Question {idx + 1} of {questions.length}</span>
                <Badge variant="outline" className="text-xs">{q.question_type.replace("_", " ")} • {q.difficulty}</Badge>
              </div>
              <p className="text-base font-medium mt-1">{q.question_text}</p>
            </CardHeader>
            <CardContent>
              {q.question_type === "mcq" && (
                <div className="space-y-2" data-testid={`q-mcq-${idx}`}>
                  {(q.options || []).map((opt, i) => (
                    <label key={i} className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition ${answers[q.question_id] === opt ? "border-primary bg-primary/5" : "hover:bg-secondary"}`}>
                      <input type="radio" name={q.question_id} checked={answers[q.question_id] === opt} onChange={() => setAnswers({ ...answers, [q.question_id]: opt })} />
                      <span className="text-sm">{opt}</span>
                    </label>
                  ))}
                </div>
              )}
              {q.question_type === "true_false" && (
                <div className="flex gap-3" data-testid={`q-tf-${idx}`}>
                  {["True", "False"].map((opt) => (
                    <label key={opt} className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-md cursor-pointer transition ${answers[q.question_id] === opt ? "border-primary bg-primary/5" : "hover:bg-secondary"}`}>
                      <input type="radio" name={q.question_id} checked={answers[q.question_id] === opt} onChange={() => setAnswers({ ...answers, [q.question_id]: opt })} />
                      <span className="text-sm font-medium">{opt}</span>
                    </label>
                  ))}
                </div>
              )}
              {q.question_type === "short_answer" && (
                <div data-testid={`q-short-${idx}`}>
                  <Label className="text-xs text-muted-foreground">Your answer</Label>
                  <Textarea
                    rows={3}
                    value={answers[q.question_id] || ""}
                    onChange={(e) => setAnswers({ ...answers, [q.question_id]: e.target.value })}
                    placeholder="Type your answer..."
                  />
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        <div className="flex justify-end gap-2 sticky bottom-4 bg-background/95 backdrop-blur p-4 rounded-md border shadow-lg">
          <span className="self-center text-sm text-muted-foreground mr-auto">{answered} / {questions.length} answered</span>
          <Button variant="outline" onClick={() => navigate("/training")}>Save & Exit</Button>
          <Button onClick={submit} disabled={submitting || answered === 0} data-testid="submit-assessment-btn">
            <CheckCircle size={16} className="mr-2" />
            {submitting ? "Grading..." : "Submit Assessment"}
          </Button>
        </div>
      </div>
    </div>
  );
}
